import * as React from 'react';

import { ErrorState, Spinner } from '@/components/ui/states';
import { PageContainer } from '@/app/shell/app-shell';

/** Suspense + error boundary wrapper for a lazily-loaded route. */
export function RouteFallback({ children }: { children: React.ReactNode }) {
  return (
    <RouteErrorBoundary>
      <React.Suspense fallback={<RouteLoading />}>{children}</React.Suspense>
    </RouteErrorBoundary>
  );
}

function RouteLoading() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner className="size-5" />
      <span className="sr-only">Loading page…</span>
    </div>
  );
}

interface BoundaryState {
  error: unknown;
}

/**
 * Catches render errors so one broken page does not blank the whole console.
 * Chunk-load failures get a reload instead of a retry: after a deploy the old
 * hashed chunk no longer exists, and only a fresh document can fix that.
 */
class RouteErrorBoundary extends React.Component<{ children: React.ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown) {
    console.error('Route render failed', error);
  }

  private isChunkError(): boolean {
    const message = this.state.error instanceof Error ? this.state.error.message : '';
    return /dynamically imported module|Loading chunk|Failed to fetch/i.test(message);
  }

  render() {
    if (this.state.error) {
      if (this.isChunkError()) {
        return (
          <PageContainer>
            <ErrorState
              error={new Error('A newer version of the console is available.')}
              onRetry={() => window.location.reload()}
            />
          </PageContainer>
        );
      }
      return (
        <PageContainer>
          <ErrorState error={this.state.error} onRetry={() => this.setState({ error: null })} />
        </PageContainer>
      );
    }
    return this.props.children;
  }
}
